import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppSwitch from '@nxsys/components/AppSwitch';
import AppUploadFile from '@nxsys/components/AppUploadFile';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Form } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { Ref, useImperativeHandle } from 'react';

export type BannerFormRef = {
  submit: () => void;
  reset: () => void;
};

export type BannerFormValues = {
  summary: string;
  startAt: string;
  endAt: string;
  backgroundImageUrl: File | string | null;
  isActive: boolean;
};

type BannerFormProps = {
  formRef?: Ref<BannerFormRef>;
  onSubmit?: (values: BannerFormValues) => void;
  defaultValues?: Partial<BannerFormValues>;
  type?: 'create' | 'edit';
};

const BannerForm: React.FC<BannerFormProps> = ({
  formRef,
  onSubmit,
  defaultValues = {},
  type = 'create',
}) => {
  const { messages } = useTypedIntl();
  const [form] = Form.useForm();

  useImperativeHandle(formRef, () => ({
    submit: () => {
      form.submit();
    },
    reset: () => {
      form.resetFields();
    },
  }));

  const onFinish = (values: any) => {
    onSubmit?.(values);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error(errorInfo);
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout="vertical"
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
      initialValues={defaultValues}
    >
      <Form.Item
        label={messages['company.banner.summary']}
        name="summary"
        rules={[{ required: true, message: messages['form.pleaseEnter'] }]}
      >
        <TextArea
          placeholder={messages['company.banner.summaryPlaceholder']}
          rows={4}
          maxLength={100}
          showCount
        />
      </Form.Item>
      <Form.Item
        label={messages['company.banner.startAt']}
        name="startAt"
        rules={[{ required: true, message: messages['form.pleaseEnter'] }]}
      >
        <AppDatePicker
          placeholder={messages['form.selectDate']}
          format="DD/MM/YYYY"
        />
      </Form.Item>
      <Form.Item
        label={messages['company.banner.endAt']}
        name="endAt"
        rules={[{ required: true, message: messages['form.pleaseEnter'] }]}
      >
        <AppDatePicker
          placeholder={messages['form.selectDate']}
          format="DD/MM/YYYY"
        />
      </Form.Item>
      {type === 'edit' && (
        <Form.Item
          label={messages['company.banner.isActive']}
          name="isActive"
          valuePropName="checked"
        >
          <AppSwitch />
        </Form.Item>
      )}
      <Form.Item
        label={messages['company.banner.backgroundImage']}
        name="backgroundImageUrl"
      >
        <AppUploadFile
          urlFile={
            typeof defaultValues?.backgroundImageUrl === 'string'
              ? defaultValues?.backgroundImageUrl
              : undefined
          }
          onChange={(file: File) => {
            form.setFieldsValue({ backgroundImageUrl: file });
          }}
          imageDimensions={{ width: 320, height: 114 }}
        />
      </Form.Item>
    </AppFormContainer>
  );
};

export default BannerForm;
